import React from "react";
import styled from "styled-components";
import { Button } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import useDeviceType from "../../leads/hooks/useDeviceType";

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  padding: 16px 0;
  min-width: 0;

  h1 {
    color: #005249;
    margin: 0;
    font-size: 28px;
    font-weight: 600;
    line-height: 1.2;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  p {
    color: #666;
    margin: 8px 0 0 0;
    font-size: 16px;
    line-height: 1.4;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  ${({ $isCompact }) =>
    $isCompact &&
    `
    padding: 12px 0;
    margin-bottom: 16px;

    h1 {
      font-size: 22px;
    }

    p {
      font-size: 14px;
    }
  `}

  ${({ $isMobile }) =>
    $isMobile &&
    `
    h1 {
      font-size: 20px;
    }
  `}
`;

const HeaderContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px 24px;
  min-width: 0;

  ${({ $isCompact }) =>
    $isCompact &&
    `
    flex-direction: column;
    gap: 12px;
  `}
`;

const TitleSection = styled.div`
  flex: 1 1 220px;
  min-width: min(100%, 220px);
  max-width: 100%;

  ${({ $isCompact }) =>
    $isCompact &&
    `
    flex: none;
    width: 100%;
    min-width: 0;
  `}
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ $isCompact }) => ($isCompact ? "stretch" : "flex-end")};
  gap: ${({ $isCompact }) => ($isCompact ? "12px" : "16px")};
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;

  ${({ $isCompact }) =>
    !$isCompact &&
    `
    flex: 0 1 auto;
    margin-left: auto;
  `}
`;

const RefreshSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ $isCompact }) => ($isCompact ? "flex-start" : "center")};
  gap: 4px;
`;

const RefreshButton = styled(Button)`
  min-width: ${({ $isCompact }) => ($isCompact ? "80px" : "100px")};
  height: ${({ $isCompact }) => ($isCompact ? "28px" : "32px")};
  align-self: ${({ $isCompact }) => ($isCompact ? "flex-start" : "center")};
`;

const Timestamp = styled.div`
  font-size: ${({ $isCompact }) => ($isCompact ? "10px" : "11px")};
  color: #666;
  text-align: ${({ $isCompact }) => ($isCompact ? "left" : "center")};
  line-height: 1.2;
`;

const DashboardHeader = ({ refreshing, refreshDashboard, lastRefreshed }) => {
  const { isMobile, isTablet } = useDeviceType();
  const isCompact = isMobile || isTablet;

  return (
    <HeaderContainer $isCompact={isCompact} $isMobile={isMobile}>
      <HeaderContent $isCompact={isCompact}>
        <TitleSection $isCompact={isCompact}>
          <h1>Dashboard</h1>
          <p>Ops & Founder report — daily operating pulse.</p>
        </TitleSection>

        <ButtonSection $isCompact={isCompact}>
          <RefreshSection $isCompact={isCompact}>
            <RefreshButton
              type="default"
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={refreshDashboard}
              loading={refreshing}
              title="Refresh Dashboard Data"
              size={isCompact ? "small" : "middle"}
              $isCompact={isCompact}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </RefreshButton>
            {lastRefreshed && (
              <Timestamp $isCompact={isCompact}>
                Last: {lastRefreshed.toLocaleTimeString()}
              </Timestamp>
            )}
          </RefreshSection>
        </ButtonSection>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default DashboardHeader;
